import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { devError } from '@/lib/devLog';

interface RestaurantContextType {
  restaurantId: string | null;
  restaurantName: string | null;
  logoPath: string | null;
  logoUpdatedAt: string | null;
  activeMenuId: string | null;
  loading: boolean;
  isAdmin: boolean;
  voiceOrderAssistEnabled: boolean;
  fewLeftEnabled: boolean;
  setActiveRestaurant: (id: string, name: string) => void;
  setRestaurantLogo: (path: string | null, updatedAt: string | null) => void;
}

const RestaurantContext = createContext<RestaurantContextType>({
  restaurantId: null,
  restaurantName: null,
  logoPath: null,
  logoUpdatedAt: null,
  activeMenuId: null,
  loading: true,
  isAdmin: false,
  voiceOrderAssistEnabled: true,
  fewLeftEnabled: true,
  setActiveRestaurant: () => {},
  setRestaurantLogo: () => {},
});

export function RestaurantProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [restaurantId, setRestaurantId] = useState<string | null>(null);
  const [restaurantName, setRestaurantName] = useState<string | null>(null);
  const [logoPath, setLogoPath] = useState<string | null>(null);
  const [logoUpdatedAt, setLogoUpdatedAt] = useState<string | null>(null);
  const [activeMenuId, setActiveMenuId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [voiceOrderAssistEnabled, setVoiceOrderAssistEnabled] = useState(true);
  const [fewLeftEnabled, setFewLeftEnabled] = useState(true);

  const fetchActiveMenuId = async (restId: string) => {
    try {
      const { data } = await supabase
        .from('menus')
        .select('id')
        .eq('restaurant_id', restId)
        .eq('status', 'active')
        .maybeSingle();
      setActiveMenuId(data?.id ?? null);
    } catch (error) {
      devError('Error fetching active menu:', error);
      setActiveMenuId(null);
    }
  };

  useEffect(() => {
    if (!user) {
      setRestaurantId(null);
      setRestaurantName(null);
      setLogoPath(null);
      setLogoUpdatedAt(null);
      setActiveMenuId(null);
      setLoading(false);
      setIsAdmin(false);
      return;
    }

    const fetchRestaurant = async () => {
      try {
        // Check if admin
        const { data: adminRole } = await supabase
          .from('user_roles')
          .select('id')
          .eq('user_id', user.id)
          .eq('role', 'admin')
          .maybeSingle();

        const userIsAdmin = !!adminRole;
        setIsAdmin(userIsAdmin);

        // Get user's restaurant_id from user_roles (restaurant_user role)
        const { data: roleData } = await supabase
          .from('user_roles')
          .select('restaurant_id')
          .eq('user_id', user.id)
          .eq('role', 'restaurant_user')
          .maybeSingle();

        if (roleData?.restaurant_id) {
          setRestaurantId(roleData.restaurant_id);
          const { data: restaurantData } = await supabase
            .from('restaurants')
            .select('*')
            .eq('id', roleData.restaurant_id)
            .single();
          setRestaurantName(restaurantData?.name ?? null);
          setLogoPath((restaurantData as any)?.logo_path ?? null);
          setLogoUpdatedAt((restaurantData as any)?.logo_updated_at ?? null);
          setVoiceOrderAssistEnabled((restaurantData as any)?.voice_order_assist_enabled ?? true);
          setFewLeftEnabled((restaurantData as any)?.few_left_enabled ?? true);
          await fetchActiveMenuId(roleData.restaurant_id);
        } else if (userIsAdmin) {
          // Admin without restaurant_user role — default to first restaurant
          const { data: restaurants } = await supabase
            .from('restaurants')
            .select('*')
            .order('created_at', { ascending: true })
            .limit(1);

          if (restaurants && restaurants.length > 0) {
            setRestaurantId(restaurants[0].id);
            setRestaurantName(restaurants[0].name);
            setLogoPath((restaurants[0] as any)?.logo_path ?? null);
            setLogoUpdatedAt((restaurants[0] as any)?.logo_updated_at ?? null);
            setVoiceOrderAssistEnabled((restaurants[0] as any)?.voice_order_assist_enabled ?? true);
            setFewLeftEnabled((restaurants[0] as any)?.few_left_enabled ?? true);
            await fetchActiveMenuId(restaurants[0].id);
          }
        }
      } catch (error) {
        devError('Error fetching restaurant context:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRestaurant();
  }, [user]);

  const setActiveRestaurant = (id: string, name: string) => {
    setRestaurantId(id);
    setRestaurantName(name);
    setLogoPath(null);
    setLogoUpdatedAt(null);
    setActiveMenuId(null);
    // Fetch active menu for new restaurant
    fetchActiveMenuId(id);
  };

  const setRestaurantLogo = (path: string | null, updatedAt: string | null) => {
    setLogoPath(path);
    setLogoUpdatedAt(updatedAt);
  };

  return (
    <RestaurantContext.Provider value={{ restaurantId, restaurantName, logoPath, logoUpdatedAt, activeMenuId, loading, isAdmin, voiceOrderAssistEnabled, fewLeftEnabled, setActiveRestaurant, setRestaurantLogo }}>
      {children}
    </RestaurantContext.Provider>
  );
}

export function useRestaurant() {
  return useContext(RestaurantContext);
}
